import { useState, useMemo, useRef, useEffect } from 'react';
import { RefreshCw, Scale, Send, Check } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@/src/infrastructure/lib/utils';
import { useReconciliationAlerts } from './hooks/useReconciliationAlerts';
import { useReconciliationFindings } from './hooks/useReconciliationFindings';
import { useReconciliationFilters } from './store/filters.store';
import { AlertsSummaryBar } from './components/AlertsSummaryBar';
import { FindingFilters } from './components/FindingFilters';
import { FindingsTable } from './components/FindingsTable';
import { SideBySideDiffViewer } from './components/SideBySideDiffViewer';
import { ResolveFindingModal } from './components/ResolveFindingModal';
import { ReplayWebhookModal } from './components/ReplayWebhookModal';
import { isFindingStale } from './components/FindingStatusBadge';
import { reconciliationKeys } from './hooks/useReconciliationAlerts';
import { useDeadLetterOutbox, deadLetterKeys } from './hooks/useDeadLetterOutbox';
import { DeadLetterOutboxTable } from './components/DeadLetterOutboxTable';
import type { QueueTab, ReconciliationFindingRow } from './types';

export default function ReconciliationPage() {
  const queryClient = useQueryClient();
  const { tab, kind, entityType, searchQuery, setTab } = useReconciliationFilters();

  const [inspectFinding, setInspectFinding] = useState<ReconciliationFindingRow | null>(null);
  const [resolveFinding, setResolveFinding] = useState<ReconciliationFindingRow | null>(null);
  const [isReplayModalOpen, setIsReplayModalOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  // Queries
  const { data: alerts, isLoading: isAlertsLoading, isRefetching: isAlertsRefetching } = useReconciliationAlerts();
  const { data: findingsData, isLoading: isFindingsLoading, isRefetching: isFindingsRefetching } = useReconciliationFindings(kind);
  const { data: deadLetterEvents, isLoading: isDeadLetterLoading } = useDeadLetterOutbox();

  const allFindings = findingsData?.items ?? [];

  // Filter findings based on active tab and search filters
  const filteredFindings = useMemo(() => {
    return allFindings.filter((row) => {
      // 1. Tab filter
      if (tab === 'STALE') {
        if (!isFindingStale(row)) return false;
      }

      // 2. Entity Type filter
      if (entityType && row.entityType.toLowerCase() !== entityType.toLowerCase()) {
        return false;
      }

      // 3. Search query filter
      if (searchQuery && searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const matchesEntity = row.entityId.toLowerCase().includes(q);
        const matchesCorrelation = row.correlationId.toLowerCase().includes(q);
        const matchesKind = row.kind.toLowerCase().includes(q);
        if (!matchesEntity && !matchesCorrelation && !matchesKind) return false;
      }

      return true;
    });
  }, [allFindings, tab, entityType, searchQuery]);

  const showToast = (msg: string) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToastMessage(msg);
    toastTimerRef.current = setTimeout(() => setToastMessage(null), 3000);
  };

  const handleManualRefresh = () => {
    queryClient.invalidateQueries({ queryKey: reconciliationKeys.all });
    queryClient.invalidateQueries({ queryKey: deadLetterKeys.all });
  };

  const isRefreshing = isAlertsRefetching || isFindingsRefetching;

  return (
    <div className="space-y-6 max-w-[1440px] mx-auto pb-12">
      {/* Toast Notification */}
      {toastMessage && (
        <div
          data-testid="toast-success"
          className="fixed bottom-6 right-6 z-50 p-4 rounded-2xl bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 font-semibold text-xs shadow-2xl flex items-center gap-2 backdrop-blur-md animate-in slide-in-from-bottom"
        >
          <Check className="w-4 h-4 text-emerald-400" />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5 mb-1">
            <div className="p-2 rounded-xl bg-primary/10 border border-primary/20 text-primary">
              <Scale className="w-5 h-5" />
            </div>
            <h1 className="text-2xl font-bold font-display text-on-surface">
              Reconciliation Findings
            </h1>
          </div>
          <p className="text-xs text-on-surface-variant max-w-xl">
            Audit and resolve state discrepancies between InviteCloud ledgers and Razorpay payment rails.
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={() => setIsReplayModalOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-border-subtle bg-surface-container/60 hover:bg-surface-container text-xs font-semibold text-on-surface hover:text-primary transition-all"
          >
            <Send className="w-3.5 h-3.5" />
            <span>Replay DLQ Webhook</span>
          </button>

          <button
            type="button"
            onClick={handleManualRefresh}
            disabled={isRefreshing}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary hover:bg-primary-hover text-on-primary font-bold text-xs shadow-md transition-all disabled:opacity-50"
          >
            <RefreshCw className={cn('w-3.5 h-3.5', isRefreshing && 'animate-spin')} />
            <span>{isRefreshing ? 'Refreshing...' : 'Refresh'}</span>
          </button>
        </div>
      </div>

      {/* System Health & Monitor Cards */}
      <AlertsSummaryBar
        alerts={alerts}
        isLoading={isAlertsLoading || isRefreshing}
        onRefresh={handleManualRefresh}
        activeTab={tab}
        onSelectTab={setTab}
        onOpenReplayModal={() => setIsReplayModalOpen(true)}
      />

      {/* Queue Tabs */}
      <div className="border-b border-border-subtle flex items-center gap-2 overflow-x-auto">
        {(
          [
            { id: 'OPEN', label: 'Open Findings', count: alerts?.openFindings },
            { id: 'STALE', label: 'Stale Alerts (>4h)', count: alerts?.staleOpenFindings },
            { id: 'DEAD_LETTER', label: 'Dead-Letter Outbox', count: deadLetterEvents?.length },
          ] as { id: QueueTab; label: string; count?: number }[]
        ).map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={cn(
              'px-4 py-3 text-xs font-bold border-b-2 transition-all whitespace-nowrap flex items-center gap-2',
              tab === t.id
                ? 'border-primary text-primary'
                : 'border-transparent text-on-surface-variant hover:text-on-surface hover:border-border-default',
            )}
          >
            <span>{t.label}</span>
            {typeof t.count === 'number' && t.count > 0 && (
              <span
                className={cn(
                  'px-1.5 py-0.2 rounded-full text-[10px] font-mono',
                  t.id === 'STALE'
                    ? 'bg-rose-500/20 text-rose-300'
                    : t.id === 'DEAD_LETTER'
                      ? 'bg-amber-500/20 text-amber-300'
                      : 'bg-primary/20 text-primary',
                )}
              >
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === 'DEAD_LETTER' ? (
        <DeadLetterOutboxTable
          events={deadLetterEvents ?? []}
          isLoading={isDeadLetterLoading}
          onSuccess={() => showToast('Outbox event requeued successfully!')}
          onError={(err) => showToast(err.message)}
        />
      ) : (
        <>
          <FindingFilters />

          {/* Data Table */}
          {isFindingsLoading ? (
            <div className="p-12 text-center text-xs text-on-surface-variant">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-primary" />
              Loading reconciliation findings...
            </div>
          ) : (
            <FindingsTable
              findings={filteredFindings}
              onInspect={(f) => setInspectFinding(f)}
              onResolve={(f) => setResolveFinding(f)}
            />
          )}
        </>
      )}

      {/* Side-by-Side Diff Inspector Drawer */}
      <SideBySideDiffViewer
        finding={inspectFinding}
        onClose={() => setInspectFinding(null)}
        onResolve={(f) => setResolveFinding(f)}
      />

      {/* Guided Resolution Modal */}
      <ResolveFindingModal
        finding={resolveFinding}
        isOpen={Boolean(resolveFinding)}
        onClose={() => setResolveFinding(null)}
        onSuccess={() => showToast('Resolution recorded successfully!')}
      />

      {/* DLQ Replay Modal */}
      <ReplayWebhookModal
        isOpen={isReplayModalOpen}
        onClose={() => setIsReplayModalOpen(false)}
      />
    </div>
  );
}
